import React, { useEffect, useState } from "react";
import { CustomModal, RowAction, AlertTost } from "#components";
import useInfinityGet from "#hooks/useInfinityGet.js";
import usePost from "#hooks/usePost.js";
import usePut from "#hooks/usePut.js";
import useDelete from "#hooks/useDelete.js";
import ENDPOINTS from "#services/endPoints.js";
import QUERY_KEYS from "#services/queryKeys.js";
import Swal from "sweetalert2";
import CustomAlert from "../../../components/CustomAlert copy";

const ContainerTypeModal = ({ open, onClose }) => {
  const [editTypeId, setEditTypeId] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [localTypes, setLocalTypes] = useState([]);

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading: isContainerTypesLoading,
  } = useInfinityGet(
    ENDPOINTS.CONTAINER_TYPE_LIST,
    QUERY_KEYS.CONTAINER_TYPE_LIST_QUERY_KEY
  );

  // POST new type
  const { mutateAsync: addTypeMutateAsync, isLoading: isAdding } = usePost(
    ENDPOINTS.ADD_CONTAINER_TYPE,
    QUERY_KEYS.ADD_CONTAINER_TYPE_QUERY_KEY
  );

  // PUT update type
  const { mutateAsync: updateTypeMutateAsync, isLoading: isUpdating } = usePut(
    "", // empty base URL, since we’ll send custom URL from data._customUrl
    QUERY_KEYS.UPDATE_CONTAINER_TYPE_QUERY_KEY
  );

  // DELETE type
  const { mutateAsync: deleteTypeMutateAsync } = useDelete(
    "", // Start with empty URL
    QUERY_KEYS.DELETE_CONTAINER_TYPE_QUERY_KEY
  );

  useEffect(() => {
    if (data?.pages) {
      const allTypes = data.pages.flatMap((page) => page?.data || []);
      setLocalTypes(allTypes);
    }
  }, [data]);

  const handleAddType = async () => {
    if (editTypeId) {
      setEditTypeId(null);
      setEditedValue("");
    }

    const input = document.getElementById("newTypeInput");
    const value = input.value.trim();

    if (!value) return AlertTost("Please enter a type name", "error");
    if (localTypes.some((t) => t.name === value))
      return AlertTost("Type already exists", "error");

    try {
      const response = await addTypeMutateAsync({ name: value });
      AlertTost(response?.message || "Type added successfully", "success");
      input.value = "";
      await refetch();
    } catch (error) {
      AlertTost(
        error?.response?.data?.message || "Failed to add type",
        "error"
      );
    }
  };

  const handleUpdateType = async (type) => {
    if (!editedValue.trim()) {
      AlertTost("Enter valid name", "error");
      return;
    }
    try {
      const response = await updateTypeMutateAsync({
        _customUrl: ENDPOINTS.UPDATE_CONTAINER_TYPE(type._id),
        name: editedValue.trim(),
      });
      AlertTost(response?.message || "Type updated successfully", "success");
      setEditTypeId(null);
      await refetch();
    } catch (error) {
      AlertTost(error?.response?.data?.message || "Failed to update", "error");
    }
  };

  const handleDeleteType = async (typeId) => {
    CustomAlert({
      title: "Are you sure you want to delete this container type?",
      confirmButtonText: "Confirm",
      icon: "question",
      onConfirm: async () => {
        try {
          const response = await deleteTypeMutateAsync({
            _customUrl: ENDPOINTS.DELETE_CONTAINER_TYPE(typeId),
          });

          AlertTost(
            response?.message || "Type deleted successfully",
            "success"
          );
          await refetch();
        } catch (error) {
          Swal.fire({
            title: "Error",
            text: error?.response?.data?.message || "Failed to delete",
            icon: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      },
    });
  };

  return (
    <CustomModal
      open={open}
      title="Manage Container Types"
      onClose={onClose}
      actionButtonTitle="Close"
      onSubmit={onClose}
    >
      <div className="common-modal-form">
        <div className="row gy-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
              <h6
                style={{
                  fontSize: "25px",
                  fontWeight: 500,
                  color: "var(--bs-black)",
                  fontFamily: "var(--primary-font)",
                }}
              >
                Container Type Listing
              </h6>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <input
                  type="text"
                  placeholder="Add new type"
                  id="newTypeInput"
                  className="form-control"
                  onFocus={() => {
                    if (editTypeId) {
                      setEditTypeId(null);
                      setEditedValue("");
                    }
                  }}
                  style={{
                    width: "200px",
                    padding: "14px",
                    borderRadius: "8px",
                  }}
                />

                <button
                  className="btn primary-btn"
                  onClick={handleAddType}
                  disabled={isAdding}
                >
                  {isAdding ? "Adding..." : "Add"}
                </button>
              </div>
            </div>

            <div
              className="card table_card p-0"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
              }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (
                  scrollHeight - scrollTop <= clientHeight + 50 &&
                  hasNextPage &&
                  !isFetchingNextPage
                ) {
                  fetchNextPage();
                }
              }}
            >
              <table className="table table-hover mb-0 align-middle">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <tr>
                    <th
                      style={{
                        width: "70%",
                        padding: "15px 30px",
                        fontSize: "16px",
                        color: "var(--bs-black)",
                        fontWeight: 500,
                        fontFamily: "var(--primary-font)",
                        lineHeight: "20.55px",
                        textTransform: "uppercase",
                        verticalAlign: "middle",
                      }}
                    >
                      Type Name
                    </th>
                    <th
                      style={{
                        width: "70%",
                        padding: "15px 30px",
                        fontSize: "16px",
                        color: "var(--bs-black)",
                        fontWeight: 500,
                        fontFamily: "var(--primary-font)",
                        lineHeight: "20.55px",
                        textTransform: "uppercase",
                        verticalAlign: "middle",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isContainerTypesLoading ? (
                    <tr>
                      <td colSpan={2} className="text-center py-3 text-muted">
                        Loading...
                      </td>
                    </tr>
                  ) : localTypes?.length > 0 ? (
                    localTypes.map((type, index) => (
                      <tr key={type._id || index}>
                        {editTypeId === type._id ? (
                          <>
                            <td style={{ padding: "15px 30px" }}>
                              <input
                                type="text"
                                value={editedValue}
                                onChange={(e) => setEditedValue(e.target.value)}
                                className="form-control"
                              />
                            </td>
                            <td
                              className="text-start"
                              style={{ padding: "15px 30px" }}
                            >
                              <button
                                className="btn primary-btn btn-sm"
                                onClick={() => handleUpdateType(type)}
                                disabled={isUpdating}
                              >
                                Save
                              </button>
                              <button
                                className="btn btn-secondary btn-sm ms-2"
                                onClick={() => setEditTypeId(null)}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "15px 30px" }}>
                              {type.name}
                            </td>
                            <td
                              className="text-end"
                              style={{ padding: "15px 30px" }}
                            >
                              <RowAction
                                activeAction={{
                                  edit: true,
                                  delete: true,
                                  view: false,
                                  status: false,
                                }}
                                onEdit={() => {
                                  setEditTypeId(type._id);
                                  setEditedValue(type.name);
                                }}
                                onDelete={() => handleDeleteType(type._id)}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="text-center py-3 text-muted">
                        No container types available.
                      </td>
                    </tr>
                  )}
                  {isFetchingNextPage && (
                    <tr>
                      <td colSpan={2} className="text-center py-2 text-muted">
                        Loading more...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default ContainerTypeModal;
